import { Fragment, lazy, memo, Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { timesheetsRoute } from 'api/navigation';

import { setAnnouncementCanShowToFalse } from 'actions/session';

import { getShowAddTeamDrawers } from 'selectors/addTeam';
import {
  getShowPayAnywhereTimesheetsModal,
  getShowQuickbooksAuthModal,
} from 'selectors/ftu';
import {
  getBannerAnnouncementForCurrentPath,
  getCurrentUser,
  getModalAnnouncementForCurrentPath,
  getShouldShowSimplifiedLeftNav,
  getShowPasswordResetModal,
} from 'selectors/session';

import AddEmployeeWarningModal from 'features/addTeam/AddEmployeeWarningModal';
import ChooseHowDrawer from 'features/addTeam/ChooseHowDrawer';
import AnnouncementBanner from 'features/banners/AnnouncementBanner';
import { selectShouldShowPastDueSubscriptionDialog } from 'features/biller/selectors';
import QuickbooksAuthModal from 'features/ftu/QuickbooksAuthModal';
import HiringCheckoutDrawer from 'features/hiringV2/HiringCheckout/HiringCheckoutDrawer';
import PasswordResetModal from 'features/modals/PasswordResetModal';
import NextIntegrationDialog from 'features/monetization/NextIntegrationDialog/NextIntegrationDialog';
import { selectShouldShowNextIntegrationDialog } from 'features/monetization/NextIntegrationDialog/selectors';
import TaskManagerCheckoutDrawer from 'features/monetization/TaskManagerCheckoutDrawer';
import { selectTaskManagerCheckoutDrawerIsOpen } from 'features/monetization/TaskManagerCheckoutDrawer/selectors';
import TipManagerCheckoutDrawer from 'features/monetization/TipManagerCheckoutDrawer';
import { selectTipManagerCheckoutDrawerIsOpen } from 'features/monetization/TipManagerCheckoutDrawer/selectors';
import AlertsDrawer from 'features/nav/LeftNav/AlertsDrawer';
import NotificationsView from 'features/notifications/NotificationsView/NotificationsView';
import CheckoutDrawer from 'features/productGrowth/components/CheckoutDrawer';
import { getCheckout } from 'features/productGrowth/components/CheckoutDrawer/selectors';
import { selectShowC2CDialogFromURL } from 'features/productGrowth/selectors';
import { HiringCheckoutInitiatedFrom } from 'features/productGrowth/types';
import AddTeamDrawer from 'features/team/drawers/AddTeamDrawer';

import ActionsListDrawer from 'components/ActionsListDrawer';
import BottomMobileInterstitial from 'components/BottomMobileInterstitial';
import BottomSheetDialog from 'components/BottomSheetDialog';
import AnnouncementModal from 'components/modals/AnnouncementModal';

import { cxHelpers } from 'util/className';

/* If your new component is increasing the main app bundle size more than 0.1 MB and does
 * not need to be loaded on the page immediately, consider lazy loading. To test the
 * production bundle run 'bun run build' and review size of 'app-bundle-{hash}.js'.
 * We should aim to keep the main app bundle under 2.3 MB'
 */
const ClickToCallDialog = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "ClickToCallDialog" */ 'features/monetization/ClickToCallDialog/ClickToCallDialog'
    )
);
const PastDueSubscriptionDialog = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "PastDueSubscriptionDialog" */ '../../monetization/PastDueSubscriptionDialog/PastDueSubscriptionDialog'
    )
);
const PayAnywhereTimesheetsModal = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "PayAnywhereTimesheetsModal" */ '../../ftu/PayAnywhereTimesheetsModal'
    )
);
const AddEmployeeSuccessModal = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "AddEmployeeSuccessModal" */ 'features/team/components/AddEmployeeSuccessModal'
    )
);
const AddIndividualDrawer = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "AddIndividualDrawer" */ 'features/addTeam/AddIndividualDrawer'
    )
);
const WebMessenger = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "MessengerRootV2" */ 'features/messengerv2/MessengerRoot'
    )
);
const TaxEntryDrawer = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "TaxEntryDrawer" */ 'features/team/drawers/TaxEntryDrawer'
    )
);
const TimecardModalV3 = lazy(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "TimecardModalV3" */ 'features/timesheets/TimesheetsPage/TimecardModal'
    )
);

// If you are adding a new Banner/Footer/Loader/Drawer and don't need a redux action to fire
// create your *Global* component here.
// If you are looking to create a new modal, please follow the patterns laid-out
// in /modals/RootModal/RootModal.jsx
const GlobalComponents = () => {
  const currentUser = useSelector(getCurrentUser);
  const showPayAnywhereTimesheetsModal = useSelector(
    getShowPayAnywhereTimesheetsModal
  );
  const showQuickbooksAuthModal = useSelector(getShowQuickbooksAuthModal);
  const modalAnnouncementForCurrentPath = useSelector(
    getModalAnnouncementForCurrentPath
  );
  const bannerAnnouncementForCurrentPath = useSelector(
    getBannerAnnouncementForCurrentPath
  );
  const showAddTeamDrawers = useSelector(getShowAddTeamDrawers);
  const showPasswordResetModal = useSelector(getShowPasswordResetModal);
  const shouldShowPastDueSubscriptionDialog = useSelector(
    selectShouldShowPastDueSubscriptionDialog
  );
  const shouldShowNextIntegrationDialog = useSelector(
    selectShouldShowNextIntegrationDialog
  );
  const TipManagerCheckoutDrawerIsOpen = useSelector(
    selectTipManagerCheckoutDrawerIsOpen
  );
  const TaskManagerCheckoutDrawerIsOpen = useSelector(
    selectTaskManagerCheckoutDrawerIsOpen
  );

  const leftNavSimplificationEnabled = useSelector(
    getShouldShowSimplifiedLeftNav
  );

  const showC2CDialogFromURL = useSelector(selectShowC2CDialogFromURL);

  const hiringCheckoutTypes: HiringCheckoutInitiatedFrom[] = [
    'HIRING_ASSISTANT_GENERIC' as HiringCheckoutInitiatedFrom,
    'HIRING_ASSISTANT_MIGRATION' as HiringCheckoutInitiatedFrom,
  ];
  const isHiring = (x: any): x is HiringCheckoutInitiatedFrom =>
    hiringCheckoutTypes.includes(x);

  const checkoutInfo = useSelector(state => getCheckout(state));
  const checkoutDrawerIsOpen =
    checkoutInfo.isOpen && !isHiring(checkoutInfo.initiatedFrom);
  const hiringCheckoutDrawerIsOpen =
    checkoutInfo.isOpen && isHiring(checkoutInfo.initiatedFrom);

  const print = useCallback(() => window.print(), []);

  const { cx } = cxHelpers('GlobalComponents');

  return (
    <div className={cx()}>
      {/* For views built in backbone; when rendered by browsers built on Chromium;
        window.print() completely crashes the tab. However, invoking window.print() from
        a react component opens the print preview without issue. That's the purpose of
        the invisible div below. */}
      <div
        className="hidden-print-button"
        style={{ display: 'none' }}
        onClick={print}
      />
      <Suspense fallback={false}>
        <Route
          path={timesheetsRoute()}
          component={lazy(
            () =>
              import(
                /* webpackPreload: true, webpackChunkName: "timesheets-global" */ 'features/nav/GlobalComponents/Timesheets'
              )
          )}
        />
        <AddEmployeeWarningModal />
        <AddEmployeeSuccessModal />
        {shouldShowPastDueSubscriptionDialog && <PastDueSubscriptionDialog />}
        {shouldShowNextIntegrationDialog && <NextIntegrationDialog />}
        {showC2CDialogFromURL && <ClickToCallDialog openedFromURLParam />}
        <AddTeamDrawer />
        <TaxEntryDrawer />
        {showAddTeamDrawers && (
          <Fragment>
            <ChooseHowDrawer />
            <AddIndividualDrawer />
          </Fragment>
        )}
        {/* ONLY IF A USER IS SIGNED IN */}
        {currentUser?.get('id') && (
          <>
            <WebMessenger />
            <TimecardModalV3 />
          </>
        )}
        {leftNavSimplificationEnabled ? (
          <AlertsDrawer />
        ) : (
          <NotificationsView />
        )}

        <ActionsListDrawer />
        {showPayAnywhereTimesheetsModal && <PayAnywhereTimesheetsModal />}
        {showQuickbooksAuthModal && <QuickbooksAuthModal />}
        {showPasswordResetModal && <PasswordResetModal />}
        <AnnouncementModal
          announcement={modalAnnouncementForCurrentPath}
          setAnnouncementCanShowToFalse={setAnnouncementCanShowToFalse}
        />
        <AnnouncementBanner
          announcement={bannerAnnouncementForCurrentPath}
          setAnnouncementCanShowToFalse={setAnnouncementCanShowToFalse}
        />
        <BottomSheetDialog>
          <BottomMobileInterstitial />
        </BottomSheetDialog>
        {TipManagerCheckoutDrawerIsOpen && <TipManagerCheckoutDrawer />}
        {TaskManagerCheckoutDrawerIsOpen && <TaskManagerCheckoutDrawer />}
        {checkoutDrawerIsOpen && <CheckoutDrawer />}
        {hiringCheckoutDrawerIsOpen && <HiringCheckoutDrawer />}
      </Suspense>
    </div>
  );
};

// IMPORTANT: We need to memoize this component to prevent re-renders
// from parent (HeaderNav) when router context changes.
// This breaks the infinite re-render loop caused by:
// 1. HeaderNav (@withCurrentRoute) re-rendering on router context changes
// 2. GlobalComponents children (e.g., TimecardModal) dispatching Redux actions
// 3. Those actions affecting the shared browserHistory between Nav.jsx and Routes.jsx Routers
// This is temporary solution to fix the issue. We need to find a more permanent solution.
export default memo(GlobalComponents);
